import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InviteByEmailOrPhoneModal, useToast } from '@wehunt/ui'
import { ApiError } from '../../shared/api'
import { isRecipientRejected } from '../teams/inviteErrors'
import { parseInviteBatch } from '../teams/inviteInput'
import { useInviteHuntGuest } from './useInviteHuntGuest'
import { trackHuntGuestInvited, type HuntGuestInviteHuntState } from './huntGuestInviteTracking'

/**
 * The organiser-only "Invite guest" action on a published hunt's detail page
 * (CLUB-972). Opens the shared invite modal and posts each address through
 * {@link useInviteHuntGuest}. Rendered into `HuntDetailPage`'s `planAction`
 * slot, which already restricts it to the hunt's creator/leaders on an upcoming
 * hunt — this component only adds the feature-flag gate at the call site.
 *
 * Mirrors the team member-invite wiring (`routes.tsx`): parse on the client,
 * send on the API, degrade a 503 to "share the join code" and a 422 to "this
 * hunt has no join code yet". The only web-specific behaviour is accepting
 * several comma-separated email addresses at once (CLUB-972 scenario 3).
 */
export function HuntGuestInviteAction({
  groupId,
  huntPlanId,
  huntState = 'planned',
}: {
  groupId: string
  huntPlanId: string
  /** Reported on each invite's RUM action; the detail page shows this on an upcoming hunt. */
  huntState?: HuntGuestInviteHuntState
}) {
  const { t, i18n } = useTranslation()
  const { showToast } = useToast()
  const invite = useInviteHuntGuest(groupId, huntPlanId)
  const [open, setOpen] = useState(false)

  const handleInvite = async (raw: { email: string; phone: string; country: string }) => {
    // A pasted list of emails is split and each invited in turn; a phone number
    // is always a single invite. One bad address refuses the whole batch and
    // nothing is sent, matching the "invalid address" acceptance criterion. The
    // split rule is shared with the plan wizard via parseInviteBatch.
    const batch = parseInviteBatch(raw)
    if (!batch.ok) return { ok: false, error: batch.error }

    try {
      for (const inv of batch.invites) {
        try {
          const { status } = await invite.mutateAsync({ ...inv, locale: i18n.language })
          trackHuntGuestInvited({ channel: inv.channel, outcome: { status }, source: 'hunt_detail', huntState })
        } catch (error) {
          trackHuntGuestInvited({ channel: inv.channel, outcome: { error }, source: 'hunt_detail', huntState })
          throw error
        }
      }
      showToast(
        batch.invites.length > 1
          ? t('routes.huntGuest.inviteSentMany', { n: batch.invites.length })
          : t('routes.huntGuest.inviteSent', {
              recipient: batch.invites[0]?.value ?? '',
            }),
      )
      return { ok: true }
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) {
        return { ok: false, error: t('routes.huntGuest.inviteUnavailable') }
      }
      if (isRecipientRejected(e)) {
        return { ok: false, error: t('routes.huntGuest.inviteRecipientRejected') }
      }
      if (e instanceof ApiError && e.status === 422) {
        return { ok: false, error: t('routes.huntGuest.inviteNoJoinCode') }
      }
      return { ok: false, error: t('routes.huntGuest.inviteError') }
    }
  }

  return (
    <>
      <button className="wh-btn wh-btn-outline" onClick={() => setOpen(true)}>
        {t('routes.huntGuest.action')}
      </button>
      {open && (
        <InviteByEmailOrPhoneModal
          onClose={() => setOpen(false)}
          onInvite={handleInvite}
          labels={{
            title: t('routes.huntGuest.modal.title'),
            subtitle: t('routes.huntGuest.modal.subtitle'),
            email: t('routes.huntGuest.modal.email'),
            orWith: t('routes.huntGuest.modal.orWith'),
            country: t('routes.huntGuest.modal.country'),
            number: t('routes.huntGuest.modal.number'),
            add: t('routes.huntGuest.modal.add'),
            sending: t('routes.huntGuest.modal.sending'),
            cancel: t('routes.huntGuest.modal.cancel'),
          }}
        />
      )}
    </>
  )
}
